import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { Box, Col, Row } from '@opendoor/atoms/box';
import { Button } from '@opendoor/atoms/button';
import { Loader } from '@opendoor/atoms/loader';
import { Text } from '@opendoor/atoms/text';
import { Toasty } from '@opendoor/atoms/toast';
import { cn } from '@opendoor/atoms/utils';
import { useAuth } from '@opendoor/auth-fe/context';
import * as Sentry from '@sentry/nextjs';
import {
  dehydrate,
  type DehydratedState,
  HydrationBoundary,
  QueryClient,
  QueryClientProvider,
} from '@tanstack/react-query';
import { requestZeusFromServer } from 'components/api/zeusServerClient';
import { pushUrl, replaceUrl } from 'components/helpers/history';
import {
  DesktopListingSortControl,
  ListingSortControl,
} from 'components/marketplace/browse/ListingSortControl';
import {
  marketplaceBrowseQueryOptions,
  type MarketplaceBrowseVariables,
} from 'components/marketplace/browse/marketplaceBrowse';
import { PopularMarkets } from 'components/marketplace/browse/PopularMarkets';
import { SegmentedControl } from 'components/marketplace/browse/SegmentedControl';
import {
  buildMarketInfoFromMarket,
  buildMarketInfoFromZone,
} from 'components/marketplace/helpers/marketInfo';
import { buildMarketplacePageKey } from 'components/marketplace/helpers/marketplacePageKey';
import { dollarsToCents } from 'components/marketplace/helpers/money';
import {
  buildPropertySearchEvent,
  PropertySearchTrigger,
} from 'components/marketplace/helpers/propertySearchEvent';
import type { ListingSortSlug } from 'components/marketplace/helpers/sortOptions';
import {
  fetchActiveBuyMarkets,
  fetchMarketRouteData,
  fetchZoneRouteData,
  parseMarketParam,
} from 'components/marketplace/helpers/ssr';
import {
  toSearchAreaInput,
  useAvailableSoonCount,
  useAvailableSoonPubliclyListedCount,
} from 'components/marketplace/helpers/useAvailableSoonCount';
import { useMapRefs } from 'components/marketplace/helpers/useMapRefs';
import { useMarketplaceFavorite } from 'components/marketplace/helpers/useMarketplaceFavorite';
import type { SearchStatus } from 'components/marketplace/helpers/useMarketplaceSearch';
import { useMarketplaceSearchController } from 'components/marketplace/helpers/useMarketplaceSearchController';
import {
  buildAddressPreviewParams,
  buildQueryParams,
  hasHomesMarketSeoFilters,
  isUuid,
  normalizeSearchVertices,
  parseQueryParams,
  parseQueryParamsFromSearch,
  parseSavedSearchId,
  removeAddressPreviewParams,
  SAVED_SEARCH_ID_PARAM,
} from 'components/marketplace/helpers/useSearchQueries';
import type { PropertySearchQuery } from 'components/marketplace/helpers/useSearchQueries';
import {
  buildPayloadFromQuery,
  getInitialPropertyTypes,
  LISTINGS_PAGE_SIZE,
  useSearchState,
} from 'components/marketplace/helpers/useSearchState';
import { useViewerMarketplaceFavorites } from 'components/marketplace/helpers/useViewerMarketplaceFavorites';
import { rememberViewedProperty } from 'components/marketplace/helpers/viewedProperties';
import {
  homesMarketRobotsDirective,
  pickHomesMarketHeroPhoto,
} from 'components/marketplace/homesMarketSeo';
import { MarketplaceViewportAreaNameDocument } from 'components/marketplace/map/__generated__/viewport-area-name.zeus';
import type { LocationSearchSelection } from 'components/marketplace/map/LocationSearch';
import { MarketplaceMapMarkers } from 'components/marketplace/map/MarketplaceMapMarkers';
import { MobileHeaderFilters } from 'components/marketplace/map/MobileHeaderFilters';
import {
  moveSearchCamera,
  moveToSearchLocation,
} from 'components/marketplace/map/moveToSearchLocation';
import {
  listingCardData,
  loadedListingCardPhoto,
  PropertyList,
  PropertyListRefreshOverlay,
} from 'components/marketplace/map/PropertyList';
import {
  type AvailabilitySelection,
  QuickAvailabilityFilter,
} from 'components/marketplace/map/QuickAvailabilityFilter';
import type { QuickBedroomsBathroomsValue } from 'components/marketplace/map/QuickBedroomsBathroomsFilter';
import { QuickFilters } from 'components/marketplace/map/QuickFilters';
import { PROPERTY_FOCUS_MAPBOX_ZOOM } from 'components/marketplace/map/refresh/clusterCamera';
import { fromGoogleZoom } from 'components/marketplace/map/refresh/googleZoom';
import { fitEnvelopeCamera } from 'components/marketplace/map/refresh/mapCamera';
import type { MapFilterBarProps } from 'components/marketplace/map/refresh/MapFilterBar';
import { countAppliedFilters } from 'components/marketplace/map/refresh/mapFilterCount';
import type {
  MapFiltersSheetProps,
  MapFiltersValue,
} from 'components/marketplace/map/refresh/MapFiltersSheet';
import type { MapPropertySelection } from 'components/marketplace/map/refresh/MapPropertyDialog';
import {
  markerPinPreviewListing,
  type PinPreviewListing,
} from 'components/marketplace/map/refresh/pinPreview';
import type { MapClusterPin, MapPin } from 'components/marketplace/map/refresh/pins';
import type { PriceRangeValue } from 'components/marketplace/map/refresh/PriceRangeFilter';
import { mapRefreshForRequest } from 'components/marketplace/map/refresh/release';
import {
  statusFilterChange,
  statusFiltersFromQuery,
} from 'components/marketplace/map/refresh/statusFilters';
import { useDraftResultCount } from 'components/marketplace/map/refresh/useDraftResultCount';
import { useMapPreviewResult } from 'components/marketplace/map/refresh/useMapPreviewResult';
import { usePriceDistribution } from 'components/marketplace/map/refresh/usePriceDistribution';
import {
  locateVisitor,
  opensOnMarketView,
  useStartArea,
} from 'components/marketplace/map/refresh/visitorStart';
import { SearchErrorToast } from 'components/marketplace/map/SearchErrorToast';
import {
  MOBILE_MINI_PDP_PANEL_CLOSED_TRANSLATE_CLASS,
  MOBILE_MINI_PDP_PANEL_HEIGHT_CLASS,
} from 'components/marketplace/map/types';
import type {
  MapLocation,
  MarketInfo,
  MarketShape,
  SearchMapCamera,
  SearchMapHandle,
  SearchMapMoveEvent,
} from 'components/marketplace/map/types';
import { useMarketplaceListingPreview } from 'components/marketplace/map/useMarketplaceListingPreview';
import { useMobileMiniPDPSwipeDismiss } from 'components/marketplace/map/useMobileMiniPDPSwipeDismiss';
import {
  getBoundsFromShape,
  getShapeFromMap,
  getViewportFromShape,
} from 'components/marketplace/map/utils';
import {
  getViewportAreaName,
  getViewportAreaNameInput,
  getViewportSamplePoints,
  getViewportSpanKm,
} from 'components/marketplace/map/viewportAreaName';
import { MarketplaceSeoHead } from 'components/marketplace/MarketplaceSeoHead';
import type { PropertyPageProps } from 'components/marketplace/property/details/loadPropertyPage';
import type { ListingPreviewPhoto } from 'components/marketplace/property/PropertyCardGallery';
import { StaticPropertyMap } from 'components/marketplace/property/StaticPropertyMap';
import type { ListingCardData } from 'components/marketplace/property/types';
import { createMarketplaceQueryCache } from 'components/marketplace/queryCache';
import {
  hasUnsavableFilters,
  toPropertySearchQuery,
} from 'components/marketplace/savedSearches/mapping';
import { SavedSearchesDropdown } from 'components/marketplace/savedSearches/SavedSearchesDropdown';
import { SaveSearchButton } from 'components/marketplace/savedSearches/SaveSearchButton';
import type { BedroomFilterValue } from 'components/marketplace/search/BedroomsBathroomsFilter';
import { toFilterString } from 'components/marketplace/search/filterHelpers';
import type { ErrorKey, FilterErrors } from 'components/marketplace/search/types';
import { DEFAULT_MARKET } from 'components/marketplace/search/utils';
import { AVAILABLE_SOON_DISCLOSURE } from 'components/marketplace/shared/availableSoon';
import {
  MOBILE_MINI_PDP_PANEL_TRANSITION_CLASS,
  SLIDE_PANEL_TRANSITION_CLASS,
} from 'components/marketplace/shared/panelTransitions';
import {
  type StagedPhotosExposureData,
  stagedPhotosExposureFromEdge,
} from 'components/marketplace/stagedPhotos/stagedPhotosEdge';
import { StagedPhotosExposure } from 'components/marketplace/stagedPhotos/StagedPhotosExposure';
import type { ActiveMarket } from 'components/marketplace/types';
import { PropertySeoHead } from 'components/properties/shared/MarketplaceLayout';
import MarketplaceMapLayout from 'components/properties/shared/MarketplaceMapLayout';
import {
  useIsDesktopViewport,
  useSmallerThanLG,
  useSmallerThanMD,
} from 'components/shared/hooks/useMediaQuery';
import type {
  CacheSafeGetServerSideProps,
  InferGetServerSidePropsType,
} from 'declarations/shared/serverSideProps';
import { useObservability } from 'helpers/observability';
import { useHydrated } from 'helpers/useHydrated';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import { useDebouncedCallback } from 'use-debounce';

const noop = () => undefined;

/** The sheet's sections, read from a search. */
const sheetValueFromQuery = (query: PropertySearchQuery): MapFiltersValue => ({
  homeTypes: {
    singleFamily: Boolean(query.singleFamily),
    townhome: Boolean(query.townhome),
    condo: Boolean(query.condo),
  },
  features: {
    canSelfTour: Boolean(query.canSelfTour),
    hasPool: Boolean(query.hasPool),
    noHoas: Boolean(query.noHoas),
    hasSingleStory: Boolean(query.hasSingleStory),
  },
  size: { min: query.minSquareFootage ?? null, max: query.maxSquareFootage ?? null },
  lot: { min: query.minLotSize ?? null, max: query.maxLotSize ?? null },
  status: statusFiltersFromQuery(query),
});

const SHEET_STATUS_PILLS = ['pendingSale', 'newlyListed', 'priceDrop', 'comingSoon'] as const;

/**
 * The search change that applying the sheet's sections makes. A status pill
 * writes its fields only when it changed, so an untouched Coming soon pill
 * leaves a search's `includeAvailableSoon` alone.
 */
const sheetQueryOverride = (
  value: MapFiltersValue,
  opened: MapFiltersValue,
): Partial<PropertySearchQuery> => ({
  singleFamily: value.homeTypes.singleFamily,
  townhome: value.homeTypes.townhome,
  condo: value.homeTypes.condo,
  canSelfTour: value.features.canSelfTour ? true : null,
  hasPool: value.features.hasPool,
  noHoas: value.features.noHoas,
  hasSingleStory: value.features.hasSingleStory,
  minSquareFootage: value.size.min,
  maxSquareFootage: value.size.max,
  minLotSize: value.lot.min,
  maxLotSize: value.lot.max,
  ...Object.assign(
    {},
    ...SHEET_STATUS_PILLS.filter((pill) => value.status[pill] !== opened.status[pill]).map((pill) =>
      statusFilterChange(pill, value.status[pill]),
    ),
  ),
});

/** The sheet with nothing chosen: every home type, no features, open ranges, no status. */
const clearedSheetValue = (): MapFiltersValue => ({
  homeTypes: { singleFamily: true, townhome: true, condo: true },
  features: { canSelfTour: false, hasPool: false, noHoas: false, hasSingleStory: false },
  size: { min: null, max: null },
  lot: { min: null, max: null },
  status: { pendingSale: false, newlyListed: false, priceDrop: false, comingSoon: false },
});

const CLUSTER_ZOOM_EVENT_DATA = { odSkipViewportSearch: true, odClusterZoom: true } as const;
const CLUSTER_LANDING_TIMEOUT_MS = 3_000;

const VIEWPORT_AREA_NAME_TIMEOUT_MS = 2_000;

// The refresh layout's components load only on a page that shows them, so the
// homes page without the release switch downloads none of them.
/** Holds the refresh map's place while its code loads, so nothing shifts. */
const RefreshMapLoading = () => <Box aria-hidden className="h-full w-full bg-od-bg-secondary" />;
const HomesMapRefresh = dynamic(
  () =>
    import('components/marketplace/map/refresh/HomesMapRefresh').then(
      (module) => module.HomesMapRefresh,
    ),
  { loading: RefreshMapLoading },
);
const MapRefreshSearchBar = dynamic(() =>
  import('components/marketplace/map/refresh/MapRefreshSearchBar').then(
    (module) => module.MapRefreshSearchBar,
  ),
);
const MapFilterBar = dynamic(() =>
  import('components/marketplace/map/refresh/MapFilterBar').then((module) => module.MapFilterBar),
);
const MapFiltersSheet = dynamic(() =>
  import('components/marketplace/map/refresh/MapFiltersSheet').then(
    (module) => module.MapFiltersSheet,
  ),
);

const MarketplaceMap = dynamic(() =>
  import('components/marketplace/map/MarketplaceMap').then((module) => module.MarketplaceMap),
);

const appliedInventoryHeading = (
  appliedAvailableSoon: boolean,
  appliedIncludeAvailableSoon: boolean,
): string => {
  if (appliedIncludeAvailableSoon) {
    return 'homes';
  }
  return appliedAvailableSoon ? 'homes available soon' : 'homes for sale';
};

const MiniPropertyDetailPage = dynamic(
  () =>
    import('components/marketplace/map/MiniPropertyDetailPage').then(
      (module) => module.MiniPropertyDetailPage,
    ),
  { loading: () => <Loader aria-label="Loading home details" size="lg" /> },
);
const MobileMiniPropertyDetailPage = dynamic(
  () =>
    import('components/marketplace/map/MobileMiniPropertyDetailPage').then(
      (module) => module.MobileMiniPropertyDetailPage,
    ),
  { loading: () => <Loader aria-label="Loading home details" size="lg" /> },
);
const MapSearchFilters = dynamic(
  () =>
    import('components/marketplace/map/SearchFilters').then((module) => module.MapSearchFilters),
  { loading: () => <Loader aria-label="Loading filters" size="lg" /> },
);

type MarketplacePageProps = InferGetServerSidePropsType<typeof getServerSideProps> & {
  initialPropertyPage?: PropertyPageProps;
};

const MarketplacePage: React.FC<MarketplacePageProps> = ({
  initialVariables,
  marketInfo: initialMarketInfo,
  queryParams,
  activeMarkets,
  canonicalOverride,
  initialSearchPathname,
  savedSearchId,
  initialZoneIds,
  initialViewportAreaName,
  ssrListingCount,
  mapRefreshEnabled,
  initialPropertyPage,
}) => {
  const router = useRouter();
  const { authentication, user } = useAuth();
  const { trackJourneyEvent } = useObservability();
  const isLoggedIn = authentication.state === 'authenticated';
  const { mapRef } = useMapRefs();
  const [locationSelection, setLocationSelection] = useState<LocationSearchSelection | null>(null);
  // Moving the map after searching a place leaves that place behind; the bar then reads "Homes in map area".
  const [mapAreaSearched, setMapAreaSearched] = useState(false);
  const cancelLocationMoveRef = useRef<(() => void) | null>(null);
  const cancelNearbyRef = useRef<(() => void) | null>(null);
  const isSmallerThanMD = useSmallerThanMD();
  const isSmallerThanLG = useSmallerThanLG();
  const isDesktopClient = useIsDesktopViewport(false);
  const isHydrated = useHydrated();
  const [groupMapPins, setGroupMapPins] = useState(true);
  const desktopMapMode = groupMapPins ? 'screen' : 'unclustered';
  const refreshMapMode = isHydrated ? desktopMapMode : 'adaptive';
  const useRefreshLayout = mapRefreshEnabled && isHydrated;
  // Preload nested controls alongside the map to avoid serial downloads.
  useEffect(() => {
    if (!mapRefreshEnabled) return;
    void import('components/marketplace/map/refresh/HomesMapRefresh');
    void import('components/marketplace/map/refresh/MapRefreshSearchBar');
    void import('components/marketplace/map/refresh/MapFilterBar');
  }, [mapRefreshEnabled]);
  const showRefreshMap = useRefreshLayout;
  const usePropertyDialog = mapRefreshEnabled;
  const mapViewport = useMemo(
    () => (mapRefreshEnabled ? getViewportFromShape(initialMarketInfo.shape) : undefined),
    [mapRefreshEnabled, initialMarketInfo.shape],
  );

  const searchState = useSearchState(queryParams);
  // A plain market view opens around the visitor, as the iOS app does, and its
  // first search covers that area. The plain homes page names no market, so it
  // opens around them wherever they are; a market page only inside its market.
  const marketBounds = useMemo(() => {
    try {
      const [[west, south], [east, north]] = getBoundsFromShape(initialMarketInfo.shape);
      return { west, south, east, north };
    } catch {
      return null;
    }
  }, [initialMarketInfo.shape]);
  const startArea = useStartArea({
    enabled:
      useRefreshLayout &&
      opensOnMarketView({
        query: queryParams,
        zoneIds: initialZoneIds,
        savedSearchId,
        propertyRoute: Boolean(initialPropertyPage),
      }),
    anywhere: canonicalOverride === '/homes',
    marketBounds,
    marketShape: initialMarketInfo.shape,
    filters: searchState.buildSearchPayload({}).listInput.filters,
  });

  const {
    hasMore,
    prefetchAfter,
    isLoadingMore,
    loadMoreError,
    loadMore,
    appliedListInput,
    mapMarkers,
    mapClusters,
    listings,
    appliedSort,
    appliedAvailableSoon,
    appliedIncludeAvailableSoon,
    appliedPage,
    isLoading,
    hasError,
    errorId,
    clearError,
    selectedLocation,
    setSelectedLocation,
    applyFilters,
    applyZoneSearch,
    applyViewport,
    applyPage,
    applySavedSearch,
    applySavedSearchOnPage,
    getSaveSearchQuery,
    activeSavedSearch,
    markActiveSavedSearch,
    clearActiveSavedSearch,
    locationLabel,
    searchPathname,
    marketInfo,
    searchLocation,
    getSearchGeneration,
    applyingSavedSearch,
  } = useMarketplaceSearchController({
    initialVariables,
    initialSearchPathname,
    activeMarkets,
    searchState,
    marketInfo: initialMarketInfo,
    initialVertices: queryParams.vertices,
    initialZoneIds,
    initialViewportAreaName,
    // `ssid` survives the hard navigation a cross-market saved-search apply
    // takes, restoring the active-search state on the destination page.
    initialSavedSearchId: savedSearchId,
    mapMode: mapRefreshEnabled ? refreshMapMode : 'markers',
    mapViewport,
  });

  const {
    result: previewMapResult,
    focusedAddressId,
    refresh: refreshPreviewMap,
  } = useMapPreviewResult({
    searchInput: appliedListInput,
    enabled: useRefreshLayout && Boolean(initialPropertyPage),
  });
  const visibleMapMarkers = previewMapResult?.markers ?? mapMarkers;
  const visibleMapClusters = previewMapResult?.clusters ?? mapClusters;

  const sellMarket = marketInfo.identifier
    ? {
        identifier: marketInfo.identifier,
        displayName: marketInfo.displayName || marketInfo.name,
      }
    : null;

  const pageCount = Math.ceil((listings?.totalCount ?? 0) / LISTINGS_PAGE_SIZE);

  // SSR uses its parsed query. After hydration, the address is the source of
  // truth because successful viewport and filter searches update it in place.
  // Reading it here keeps copied or new-tab page links on the visible search
  // without serializing the default values returned by `getCanonical()`.
  const hrefForPage = useCallback(
    (page: number) => {
      const currentQuery =
        typeof window === 'undefined'
          ? queryParams
          : parseQueryParamsFromSearch(window.location.search);
      return appendSavedSearchId(
        buildCanonicalSearchPath(
          searchPathname,
          { ...currentQuery, page, preview_aid: null },
          { seedFromUrl: false },
        ),
        typeof window === 'undefined'
          ? savedSearchId
          : new URLSearchParams(window.location.search).get(SAVED_SEARCH_ID_PARAM),
      );
    },
    [queryParams, savedSearchId, searchPathname],
  );

  // Fires only once the page's results have landed: `applyPage` abandons
  // superseded and failed fetches, and click-intent semantics would inflate
  // depth for exactly the heavy users the metric exists for. A page turn
  // never changes totalCount, so the value captured at click time stays
  // truthful when the search resolves. Filters are deliberately absent: they
  // would come from draft state, which a page turn does not apply.
  const trackPageTurn = useCallback(
    (page: number) => {
      trackJourneyEvent(
        'marketplaceProperty',
        'propertySearch',
        buildPropertySearchEvent({
          trigger: PropertySearchTrigger.PAGE_CHANGE,
          filters: { minBeds: null, minBaths: null, minPrice: null, maxPrice: null },
          market: marketInfo.name,
          resultsCount: listings?.totalCount,
          sort: appliedSort,
          page,
        }),
      );
    },
    [trackJourneyEvent, appliedSort, marketInfo.name, listings?.totalCount],
  );

  const handlePageSelect = useCallback(
    (page: number) => {
      void applyPage(page).then((status) => {
        if (status === 'applied') {
          trackPageTurn(page);
        }
      });
    },
    [applyPage, trackPageTurn],
  );

  // Set by search-running handlers and consumed by the results-settle effect
  // below, so `resultsCount` reflects the search the interaction ran rather
  // than the previous results still on screen. Starts null so hydration and
  // the SSR-seeded initial results never fire an event.
  const pendingSearchTriggerRef = useRef<PropertySearchTrigger | null>(null);

  // A cluster zoom knows where it lands, so the search for that camera starts
  // with the zoom and shows its results once the camera lands. Landing there
  // then needs no search of its own.
  const clusterSearchRef = useRef<{
    settle: (landed: boolean) => void;
    /** The search's number; another search started since has a higher one. */
    generation: number;
  } | null>(null);
  const dropClusterSearch = () => {
    const pending = clusterSearchRef.current;
    if (!pending) return;
    clusterSearchRef.current = null;
    pending.settle(false);
    // Its results never show, so neither does its search event. A trigger
    // another search set since then stays.
    if (pendingSearchTriggerRef.current === PropertySearchTrigger.MAP_MOVE) {
      pendingSearchTriggerRef.current = null;
    }
  };
  // A zoom that another action stopped still raises its move end once the map
  // idles; that landing searches nothing, since the action searches for itself.
  const abandonedClusterZoomRef = useRef(false);
  const abandonClusterSearch = () => {
    if (clusterSearchRef.current) abandonedClusterZoomRef.current = true;
    dropClusterSearch();
  };
  const mapFilterChangeIdRef = useRef(0);
  const applyMapFilterChange = async (
    override: Parameters<typeof applyFilters>[0],
    changeId: number,
  ) => {
    if (changeId !== mapFilterChangeIdRef.current) return;
    pendingSearchTriggerRef.current = PropertySearchTrigger.QUICK_FILTER;
    const status = await applyFilters(override);
    if (status !== 'applied' && changeId === mapFilterChangeIdRef.current) {
      searchState.applyCanonical(parseQueryParamsFromSearch(window.location.search));
    }
  };
  const applyPriceSoon = useDebouncedCallback((next: PriceRangeValue, changeId: number) => {
    void applyMapFilterChange({ minListPrice: next.min, maxListPrice: next.max }, changeId);
  }, 300);
  const cancelMapFilterChanges = useCallback(() => {
    mapFilterChangeIdRef.current += 1;
    applyPriceSoon.cancel();
  }, [applyPriceSoon]);
  useEffect(() => {
    window.addEventListener('popstate', cancelMapFilterChanges);
    return () => {
      window.removeEventListener('popstate', cancelMapFilterChanges);
      cancelMapFilterChanges();
    };
  }, [cancelMapFilterChanges]);

  // Read at rollback time so a pick that never landed is undone against the
  // ordering currently on screen, not the one captured when it was made.
  const appliedAvailableSoonRef = useRef(appliedAvailableSoon);
  const appliedIncludeAvailableSoonRef = useRef(appliedIncludeAvailableSoon);
  const appliedSortRef = useRef(appliedSort);
  const segmentPickSeqRef = useRef(0);
  useEffect(() => {
    appliedAvailableSoonRef.current = appliedAvailableSoon;
    appliedIncludeAvailableSoonRef.current = appliedIncludeAvailableSoon;
  }, [appliedAvailableSoon, appliedIncludeAvailableSoon]);
  useEffect(() => {
    appliedSortRef.current = appliedSort;
  }, [appliedSort]);

  const handleResetFilters = () => {
    cancelMapFilterChanges();
    setErrors(undefined);
    searchState.resetFilters();
    clearActiveSavedSearch();
    // Reset runs the unfiltered search immediately so results never sit
    // desynced from the cleared modal state; the settle effect reports the
    // count this search actually produced. The state setters above do not land
    // before the search builds its payload, so the reset shape goes in as an
    // explicit override.
    pendingSearchTriggerRef.current = PropertySearchTrigger.RESET;
    void applyFilters({ ...searchState.resetQuery });
  };

  // Shows a saved search's area while its results load. The move carries no
  // viewport search: the saved search already searches this area.
  const moveMapToSavedSearchArea = (shape: MarketShape) => {
    const map = mapRef.current;
    const size = map?.getVisibleSize?.();
    if (!map || !size) return;
    let bounds: ReturnType<typeof getBoundsFromShape>;
    try {
      bounds = getBoundsFromShape(shape);
    } catch {
      return;
    }
    const [[west, south], [east, north]] = bounds;
    const { center, zoom } = fitEnvelopeCamera({ west, south, east, north }, size, {});
    cancelLocationMoveRef.current = moveSearchCamera({
      map,
      camera: { center, zoom: fromGoogleZoom(zoom) },
      onComplete: noop,
    });
  };

  const handleApplySavedSearch = (savedSearch: Parameters<typeof applySavedSearch>[0]) => {
    abandonClusterSearch();
    cancelMapFilterChanges();
    const changeId = mapFilterChangeIdRef.current;
    cancelLocationSearch();
    mapRef.current?.stop();
    cancelViewportSearch();
    setErrors(undefined);
    setLocationSelection(null);
    setMapAreaSearched(false);
    pendingSearchTriggerRef.current = PropertySearchTrigger.SAVED_SEARCH;
    // The new map moves to the saved area on this page; the legacy layout loads
    // a new page for another market, zone or drawn area.
    const apply = useRefreshLayout
      ? applySavedSearchOnPage(savedSearch, moveMapToSavedSearchArea)
      : applySavedSearch(savedSearch);
    return apply.then(() => {
      if (useRefreshLayout && changeId === mapFilterChangeIdRef.current) {
        searchState.applyCanonical(parseQueryParamsFromSearch(window.location.search));
      }
    });
  };

  const {
    minBeds,
    maxBeds,
    minBaths,
    minPrice,
    maxPrice,
    singleFamily,
    condo,
    townhome,
    noHoas,
    availableSoon,
    includeAvailableSoon,
    singleStory,
    mustHavePool,
    minYearBuilt,
    maxYearBuilt,
    minLotSize,
    maxLotSize,
    minSquareFootage,
    maxSquareFootage,
  } = searchState.get;

  // Fires the pending propertySearch event once its results settle.
  // `listings.totalCount` still reflects the previous search at interaction
  // time (the fetch is async), so handlers stash the trigger and this effect
  // reports the count the interaction actually produced — once per settled
  // search, however many renders or debounced map moves preceded it.
  useEffect(() => {
    if (hasError) {
      pendingSearchTriggerRef.current = null;
      return;
    }
    const trigger = pendingSearchTriggerRef.current;
    if (trigger == null || isLoading || listings == null) {
      return;
    }
    pendingSearchTriggerRef.current = null;
    trackJourneyEvent(
      'marketplaceProperty',
      'propertySearch',
      buildPropertySearchEvent({
        trigger,
        filters: { minBeds, minBaths, minPrice, maxPrice },
        market: marketInfo.name,
        resultsCount: listings.totalCount,
        sort: appliedSort,
      }),
    );
  }, [
    appliedSort,
    hasError,
    isLoading,
    listings,
    minBeds,
    minBaths,
    minPrice,
    maxPrice,
    marketInfo.name,
    trackJourneyEvent,
  ]);

  // UX states
  const [errors, setErrors] = useState<FilterErrors>();
  const [showList, setShowList] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [hasOpenedFilters, setHasOpenedFilters] = useState(false);
  const [showPdp, setShowPdp] = useState<boolean>(false);
  const customerId = user?.customerUuid;
  const { likedPropertyIds, initialFavoritesPending, onFavoriteChange } =
    useViewerMarketplaceFavorites(customerId, isLoggedIn);

  const isZoneRoute = searchPathname.startsWith('/homes/zid_');

  const pagination =
    pageCount > 1
      ? {
          currentPage: appliedPage,
          totalPages: pageCount,
          hrefForPage,
          onPageSelect: handlePageSelect,
        }
      : undefined;

  const [selectedAddressId, setSelectedAddressId] = useState<string | null>(
    queryParams.preview_aid ?? null,
  );
  const resultsFocusRef = useRef<HTMLElement | null>(null);
  const [mapPropertySelection, setMapPropertySelection] = useState<MapPropertySelection | null>(
    null,
  );
  const propertyPreview = useMarketplaceListingPreview(
    usePropertyDialog ? null : selectedAddressId,
  );
  const {
    data: propertyDetails,
    isLoadingError: propertyPreviewLoadingFailed,
    isFetching: propertyPreviewIsFetching,
    isSuccess: propertyPreviewSucceeded,
    refetch: refetchPropertyPreview,
  } = propertyPreview;
  const loadingAddressId = propertyPreviewIsFetching ? selectedAddressId : null;
  const { isFavoriteLoading, isLiked, onToggleFavorite } = useMarketplaceFavorite({
    addressId: propertyDetails?.addressToken,
    initialFavoritePending: initialFavoritesPending,
    initialIsLiked: propertyDetails?.addressToken
      ? likedPropertyIds?.has(propertyDetails.addressToken)
      : undefined,
    origin: 'GALLERY',
    onFavoriteChange,
    priceCents:
      propertyDetails?.listPrice == null ? null : dollarsToCents(propertyDetails.listPrice),
  });
  const propertyDetailsWithFavorite = propertyDetails
    ? { ...propertyDetails, isFavoriteLoading, isLiked, onToggleFavorite }
    : null;
  const previewAddressId = propertyDetails?.addressToken;
  const handleFetchPropertyDetails = useCallback(
    (addressId: string) => {
      if (addressId === selectedAddressId) {
        if (propertyPreviewLoadingFailed || (!propertyDetails && !propertyPreviewIsFetching)) {
          void refetchPropertyPreview();
        } else if (propertyDetails) {
          setShowPdp(true);
        }
        return;
      }
      setSelectedAddressId(addressId);
    },
    [
      propertyDetails,
      propertyPreviewLoadingFailed,
      propertyPreviewIsFetching,
      refetchPropertyPreview,
      selectedAddressId,
      setShowPdp,
      setSelectedAddressId,
    ],
  );

  useEffect(() => {
    if (usePropertyDialog || !selectedAddressId || previewAddressId !== selectedAddressId) {
      return;
    }
    // oxlint-disable-next-line react/set-state-in-effect -- react19-baseline: pre-existing, do not copy. Derive during render or set from the event instead
    setShowFilters(false);
    setShowPdp(true);
    // Only the refreshed map shows viewed homes, so only it remembers them.
    if (mapRefreshEnabled) rememberViewedProperty(previewAddressId);
    trackJourneyEvent('marketplaceProperty', 'detailPageView', {
      addressUuid: previewAddressId,
    });
    const params = new URLSearchParams(window.location.search);
    if (params.get('preview_aid') !== previewAddressId) {
      const urlParams = buildAddressPreviewParams(previewAddressId);
      pushUrl(`${window.location.pathname}?${urlParams}`);
    }
  }, [
    mapRefreshEnabled,
    previewAddressId,
    selectedAddressId,
    trackJourneyEvent,
    usePropertyDialog,
  ]);

  useEffect(() => {
    const previewUnavailable =
      propertyPreviewLoadingFailed || (propertyPreviewSucceeded && !propertyDetails);
    if (selectedAddressId && !propertyPreviewIsFetching && previewUnavailable) {
      // oxlint-disable-next-line react/set-state-in-effect -- react19-baseline: pre-existing, do not copy. Derive during render or set from the event instead
      setShowPdp(false);
    }
  }, [
    propertyDetails,
    propertyPreviewLoadingFailed,
    propertyPreviewIsFetching,
    propertyPreviewSucceeded,
    selectedAddressId,
  ]);

  const handleError = (key: ErrorKey, val: string) => {
    if (val === '') {
      setErrors((previousErrors) => {
        if (!previousErrors?.[key]) {
          return previousErrors;
        }
        const nextErrors = { ...previousErrors };
        delete nextErrors[key];
        return Object.keys(nextErrors).length > 0 ? nextErrors : undefined;
      });
      return;
    }

    setErrors((previousErrors) => ({ ...previousErrors, [key]: val }));
  };

  useEffect(() => {
    if (initialPropertyPage) return;
    const pathname = window.location.pathname.toLowerCase();
    const query = parseQueryParamsFromSearch(window.location.search);
    // Back/Forward can change location before hydration attaches the history listener.
    if (
      (pathname !== initialSearchPathname.toLowerCase() &&
        pathname !== canonicalOverride?.toLowerCase()) ||
      JSON.stringify(normalizeSearchVertices(query.vertices)) !==
        JSON.stringify(normalizeSearchVertices(queryParams.vertices))
    ) {
      window.location.reload();
      return;
    }
    if (initialMarketInfo.identifier && !canonicalOverride) {
      const canonicalPath = buildCanonicalSearchPath(initialSearchPathname, query);
      if (`${window.location.pathname}${window.location.search}` !== canonicalPath) {
        replaceUrl(canonicalPath);
      }
    }
  }, [
    canonicalOverride,
    initialPropertyPage,
    initialMarketInfo.identifier,
    initialSearchPathname,
    queryParams.vertices,
  ]);

  useEffect(() => {
    const handleHistoryChange = () => {
      const addressId = new URLSearchParams(window.location.search).get('preview_aid');
      setShowPdp(false);
      setSelectedAddressId(addressId && isUuid(addressId) ? addressId : null);
    };

    window.addEventListener('popstate', handleHistoryChange);
    return () => window.removeEventListener('popstate', handleHistoryChange);
  }, []);

  // Smaller filter menu on desktop that allows for immediate querying
  const handleQuickFilterChange = ({
    bedrooms,
    maxBedrooms,
    bathrooms,
  }: QuickBedroomsBathroomsValue) => {
    const nextBedrooms = Number(bedrooms);
    const nextMaxBedrooms = Number(maxBedrooms) || null;
    const nextBathrooms = Number(bathrooms);
    searchState.set.setBedrooms(nextBedrooms);
    searchState.set.setMaxBedrooms(nextMaxBedrooms ?? 0);
    searchState.set.setBathrooms(nextBathrooms);
    pendingSearchTriggerRef.current = PropertySearchTrigger.QUICK_FILTER;
    void (async () => {
      const status = await applyFilters({
        bedrooms: nextBedrooms,
        maxBedrooms: nextMaxBedrooms,
        bathrooms: nextBathrooms,
      });
      if (status !== 'failed') {
        return;
      }
      const appliedQuery = parseQueryParamsFromSearch(window.location.search);
      searchState.set.setBedrooms((current) =>
        current === nextBedrooms ? (appliedQuery.bedrooms ?? 0) : current,
      );
      searchState.set.setMaxBedrooms((current) =>
        current === (nextMaxBedrooms ?? 0) ? (appliedQuery.maxBedrooms ?? 0) : current,
      );
      searchState.set.setBathrooms((current) =>
        current === nextBathrooms ? (appliedQuery.bathrooms ?? 0) : current,
      );
    })();
  };

  const handleQuickPriceChange = (nextMinPrice: number | null, nextMaxPrice: number | null) => {
    handleError('minPrice', '');
    handleError('maxPrice', '');
    searchState.set.setMinPrice(nextMinPrice);
    searchState.set.setMaxPrice(nextMaxPrice);
    pendingSearchTriggerRef.current = PropertySearchTrigger.QUICK_FILTER;
    void (async () => {
      const status = await applyFilters({
        minListPrice: nextMinPrice,
        maxListPrice: nextMaxPrice,
      });
      if (status !== 'failed') {
        return;
      }
      const appliedQuery = parseQueryParamsFromSearch(window.location.search);
      searchState.set.setMinPrice((current) =>
        current === nextMinPrice ? (appliedQuery.minListPrice ?? null) : current,
      );
      searchState.set.setMaxPrice((current) =>
        current === nextMaxPrice ? (appliedQuery.maxListPrice ?? null) : current,
      );
    })();
  };

  // Mirrors handleSortChange's contract: the control follows the pending pick
  // for instant feedback, but a failed or superseded switch rolls back to the
  // segments of the results on screen. The guard is the pick's identity, not
  // its value — a re-pick of the same segments while an earlier search is still
  // in flight must not be stomped by that search's settle.
  const handleAvailabilitySelectionChange = (selection: AvailabilitySelection) => {
    const nextAvailableSoon = selection.availableSoon && !selection.forSale;
    const nextIncludeAvailableSoon = selection.availableSoon && selection.forSale;
    const pickSequence = ++segmentPickSeqRef.current;
    searchState.set.setAvailableSoon(nextAvailableSoon);
    searchState.set.setIncludeAvailableSoon(nextIncludeAvailableSoon);
    pendingSearchTriggerRef.current = PropertySearchTrigger.QUICK_FILTER;
    void (async () => {
      const status = await applyFilters({
        availableSoon: nextAvailableSoon,
        includeAvailableSoon: nextIncludeAvailableSoon,
      });
      if (status === 'applied' || segmentPickSeqRef.current !== pickSequence) {
        return;
      }
      searchState.set.setAvailableSoon(appliedAvailableSoonRef.current);
      searchState.set.setIncludeAvailableSoon(appliedIncludeAvailableSoonRef.current);
    })();
  };

  const handleAvailableSoonChange = (nextAvailableSoon: boolean) => {
    handleAvailabilitySelectionChange(
      nextAvailableSoon
        ? { forSale: false, availableSoon: true }
        : { forSale: true, availableSoon: false },
    );
  };

  const handleInventorySegmentChange = (segment: string) => {
    handleAvailableSoonChange(segment === 'availableSoon');
  };

  // Market- or zone-level on purpose: whether an area offers coming-soon homes
  // doesn't change as the map pans across viewport vertices.
  const availableSoonCountWhere = toSearchAreaInput({
    market: marketInfo.identifier,
    vertices: null,
    zoneIds: isZoneRoute ? [searchPathname.slice('/homes/zid_'.length)] : searchLocation.zoneIds,
  });
  const { data: availableSoonCount } = useAvailableSoonCount({
    where: availableSoonCountWhere,
  });
  // The MLS-overlap read picks the control's shape; a failed or unresolved
  // read keeps the exclusive control, so an uncountable pool can never present
  // checkboxes.
  const { data: availableSoonPubliclyListedCount } = useAvailableSoonPubliclyListedCount({
    where: availableSoonCountWhere,
  });
  const availabilityControlMode: 'radio' | 'checkbox' =
    availableSoonPubliclyListedCount === 0 ? 'checkbox' : 'radio';
  const overlapResolved = availableSoonPubliclyListedCount != null;
  // Keep the control visible while an available-soon search is on screen even
  // if the last home lists mid-session, so the way back never unmounts.
  const showInventorySegment = (availableSoon || (availableSoonCount ?? 0) > 0) && overlapResolved;
  const mobileInventoryControl = showInventorySegment ? (
    <SegmentedControl
      aria-label="Listing availability"
      analyticsName="marketplace-inventory-segment"
      size="sm"
      value={availableSoon ? 'availableSoon' : 'forSale'}
      onValueChange={handleInventorySegmentChange}
    >
      <SegmentedControl.Item value="forSale">For sale</SegmentedControl.Item>
      <SegmentedControl.Item value="availableSoon">Available soon</SegmentedControl.Item>
    </SegmentedControl>
  ) : null;
  const desktopInventoryControl = showInventorySegment ? (
    <QuickAvailabilityFilter
      mode={availabilityControlMode}
      availableSoon={availableSoon}
      includeAvailableSoon={includeAvailableSoon}
      onChange={handleAvailabilitySelectionChange}
    />
  ) : null;

  // The control renders `appliedSort`, so the displayed ordering only changes
  // when results land. A pick that never lands still has to be undone, or a
  // later unrelated search would inherit an ordering the user never got and
  // apply it as if the filter change had asked for it. `appliedSort` is the
  // only safe anchor: it always holds a landed value, so neither concurrent
  // picks nor a superseding search that itself fails can leave an intermediate
  // ordering behind.
  const handleSortChange = (nextSort: ListingSortSlug) => {
    if (nextSort === appliedSort && nextSort === searchState.get.sort) {
      return;
    }
    searchState.set.setSort(nextSort);
    pendingSearchTriggerRef.current = PropertySearchTrigger.SORT_CHANGE;
    void (async () => {
      const status = await applyFilters({ sort: nextSort });
      // A superseding search that carries this same sort commits it itself, so
      // by the time this resolves the ordering is already live and there is
      // nothing to undo. Rolling back on the stale closure value here would
      // revert a selection that did land.
      if (status === 'applied' || appliedSortRef.current === nextSort) {
        return;
      }
      searchState.set.setSort((current) =>
        current === nextSort ? appliedSortRef.current : current,
      );
    })();
  };

  const handleMobileFilterClick = () => {
    const map = mapRef.current;
    const coordinates = selectedLocation?.coordinates;
    if (map && selectedLocation && coordinates) {
      cancelLocationSearch();
      map.stop();
      cancelViewportSearch();
      cancelLocationMoveRef.current = moveToSearchLocation({
        map,
        location: { ...selectedLocation, coordinates },
        // Filters keep the selected zone constraint; camera movement must not replace it.
        onComplete: () => {},
      });
    }
    pendingSearchTriggerRef.current = PropertySearchTrigger.FILTER_APPLY;
    void (async () => {
      await applyFilters();
      setShowFilters(false);
    })();
  };

  // Desktop full-filter "Search" CTA. Wraps `applyFilters` so the press event
  // isn't forwarded as a filter override.
  const handleApplyFilters = () => {
    cancelMapFilterChanges();
    const changeId = mapFilterChangeIdRef.current;
    pendingSearchTriggerRef.current = PropertySearchTrigger.FILTER_APPLY;
    void applyFilters().then((status) => {
      if (useRefreshLayout && status !== 'applied' && changeId === mapFilterChangeIdRef.current) {
        searchState.applyCanonical(parseQueryParamsFromSearch(window.location.search));
      }
    });
  };

  const handleCloseFilters = useCallback(() => {
    setShowFilters(false);
  }, [setShowFilters]);

  // The trigger is stashed here, inside the debounced apply, rather than in
  // `handleOnMoveEnd`, so a burst of map moves marks one pending search per
  // debounce settle instead of one per `moveend`. Returns true when a search
  // started.
  const setSearchAreaFromMapBounds = useCallback(
    (
      map: Pick<SearchMapHandle, 'getBounds' | 'isMoving'>,
      trigger = PropertySearchTrigger.MAP_MOVE,
      location?: MapLocation,
    ) => {
      if (map.isMoving()) {
        return false;
      }
      const shape = getShapeFromMap(map);
      if (!normalizeSearchVertices(shape)) return false;
      cancelMapFilterChanges();
      const changeId = mapFilterChangeIdRef.current;
      pendingSearchTriggerRef.current = trigger;
      void applyViewport(shape, location).then(() => {
        if (useRefreshLayout && changeId === mapFilterChangeIdRef.current) {
          searchState.applyCanonical(parseQueryParamsFromSearch(window.location.search));
        }
      });
      return true;
    },
    [applyViewport, cancelMapFilterChanges, searchState, useRefreshLayout],
  );

  // The viewport search the latest map settle asked for: due while the
  // debounce runs, then started. Each settle that asks for a search replaces
  // it, so it never stands for a search for an earlier camera.
  const [cameraSearch, setCameraSearch] = useState<'due' | 'started' | null>(null);
  // A search starts in the same update that marks it started, so `isLoading`
  // then belongs to it or to a later search, never to an earlier one.
  const cameraSearchPending = cameraSearch === 'due' || (cameraSearch === 'started' && isLoading);
  const searchSettledCamera = useCallback(
    (map: Pick<SearchMapHandle, 'getBounds' | 'isMoving'>) => {
      setCameraSearch(setSearchAreaFromMapBounds(map) ? 'started' : null);
    },
    [setSearchAreaFromMapBounds, setCameraSearch],
  );

  const handleMapReady = useCallback(
    (map: SearchMapHandle) => {
      if (
        !initialPropertyPage &&
        !normalizeSearchVertices(queryParams.vertices) &&
        !queryParams.page
      ) {
        setSearchAreaFromMapBounds(map);
      }
    },
    [initialPropertyPage, queryParams.vertices, queryParams.page, setSearchAreaFromMapBounds],
  );

  const closePropertyDetails = useCallback(() => {
    if (initialPropertyPage) {
      const params = buildQueryParams({ ...queryParams, preview_aid: null });
      void router.push(`${initialSearchPathname}?${params}`);
      return;
    }
    setShowPdp(false);
    setSelectedAddressId(null);
    setMapPropertySelection(null);
    const urlParams = removeAddressPreviewParams();
    replaceUrl(`${window.location.pathname}?${urlParams}`);
  }, [
    initialPropertyPage,
    initialSearchPathname,
    queryParams,
    router,
    setShowPdp,
    setSelectedAddressId,
    setMapPropertySelection,
  ]);

  const mobileMiniPdpSwipe = useMobileMiniPDPSwipeDismiss(
    closePropertyDetails,
    showPdp && Boolean(propertyDetails),
  );

  // A trackpad gesture can settle several times; one search follows the last settle. The
  // refresh map waits less so its pins regroup with the new results soon after the gesture.
  const debouncedSetSearchAreaFromViewport = useDebouncedCallback(
    searchSettledCamera,
    useRefreshLayout ? 150 : 200,
  );
  const cancelViewportSearch = useCallback(() => {
    debouncedSetSearchAreaFromViewport.cancel();
    setCameraSearch(null);
  }, [debouncedSetSearchAreaFromViewport, setCameraSearch]);

  const debouncedSetSearchAreaFromLocation = useDebouncedCallback(setSearchAreaFromMapBounds, 200);

  const cancelLocationMove = useCallback(() => {
    cancelLocationMoveRef.current?.();
    cancelLocationMoveRef.current = null;
    debouncedSetSearchAreaFromLocation.cancel();
  }, [debouncedSetSearchAreaFromLocation]);

  const cancelLocationSearch = useCallback(() => {
    cancelNearbyRef.current?.();
    cancelNearbyRef.current = null;
    cancelLocationMove();
  }, [cancelLocationMove]);

  useEffect(
    () => () => {
      cancelLocationSearch();
      debouncedSetSearchAreaFromViewport.cancel();
    },
    [cancelLocationSearch, debouncedSetSearchAreaFromViewport],
  );

  const handleNearbyStart = useCallback(
    (cancel: () => void) => {
      cancelLocationSearch();
      cancelNearbyRef.current = cancel;
    },
    [cancelLocationSearch],
  );

  // A map that goes away, with the layout or the page, never lands.
  useEffect(() => {
    if (!useRefreshLayout) return undefined;
    return () => {
      clusterSearchRef.current?.settle(false);
      clusterSearchRef.current = null;
    };
  }, [useRefreshLayout]);
  const landClusterSearch = (map: Pick<SearchMapHandle, 'getBounds' | 'isMoving'>) => {
    const pending = clusterSearchRef.current;
    if (!pending) return false;
    clusterSearchRef.current = null;
    if (getSearchGeneration() !== pending.generation) {
      // A filter, sort or page search replaced this one during the zoom and
      // searched the earlier area, so the landed camera searches now, with the
      // filters in use now. A search that starts after the landing needs no help.
      pending.settle(false);
      searchSettledCamera(map);
      return true;
    }
    // Shows the results after this frame, once the pins have settled on the landed camera.
    window.requestAnimationFrame(() => pending.settle(true));
    return true;
  };
  const handleClusterZoom = (camera: SearchMapCamera & { zoom: number }): boolean => {
    const map = mapRef.current;
    if (!map) return false;
    dropClusterSearch();
    abandonedClusterZoomRef.current = false;
    cancelLocationMove();
    const area = map.getSearchAreaAt?.(camera);
    const shape = area ? getShapeFromMap({ getBounds: () => area }) : null;
    if (!shape || !normalizeSearchVertices(shape)) {
      // Without a known destination, the settled camera searches as usual.
      map.flyTo(camera);
      return true;
    }
    let settle: (landed: boolean) => void = noop;
    const landed = new Promise<boolean>((resolve) => {
      settle = resolve;
    });
    let reportStatus: (status: SearchStatus) => void = noop;
    const status = new Promise<SearchStatus>((resolve) => {
      reportStatus = resolve;
    });
    cancelMapFilterChanges();
    debouncedSetSearchAreaFromViewport.cancel();
    const changeId = mapFilterChangeIdRef.current;
    pendingSearchTriggerRef.current = PropertySearchTrigger.MAP_MOVE;
    setCameraSearch('started');
    const search = applyViewport(shape, undefined, {
      commitAfter: landed,
      onSearched: reportStatus,
    });
    clusterSearchRef.current = { settle, generation: getSearchGeneration() };
    void search.then(async () => {
      // A dropped or replaced search leaves the filters to the search that replaced it.
      if ((await status) !== 'superseded' && changeId === mapFilterChangeIdRef.current) {
        searchState.applyCanonical(parseQueryParamsFromSearch(window.location.search));
      }
    });
    map.flyTo(camera, CLUSTER_ZOOM_EVENT_DATA);
    // A camera that is already there raises no move end.
    if (!map.isMoving()) landClusterSearch(map);
    // A landing that never comes drops the early results; the move end then searches as usual.
    window.setTimeout(() => {
      if (clusterSearchRef.current?.settle === settle) dropClusterSearch();
    }, CLUSTER_LANDING_TIMEOUT_MS);
    return true;
  };

  const handleOnMoveEnd = (e: SearchMapMoveEvent) => {
    if (e.odClusterZoom === true) {
      if (landClusterSearch(e.target)) return;
      if (abandonedClusterZoomRef.current) {
        abandonedClusterZoomRef.current = false;
        return;
      }
    }
    // The visitor moved the map before the zoom landed: its early results go,
    // and this settle searches as usual.
    dropClusterSearch();
    if (useRefreshLayout && e.odPreviewAddressId) {
      if (initialPropertyPage) refreshPreviewMap(getShapeFromMap(e.target), e.odPreviewAddressId);
      else setSearchAreaFromMapBounds(e.target);

      return;
    }
    // Location selection searches its settled bounds through its completion
    // callback. A cluster landing whose early search was dropped searches here.
    if (e.odSkipViewportSearch === true && e.odClusterZoom !== true) {
      return;
    }
    cancelLocationMove();
    // The legacy layout keeps its place while the map moves; the new map searches the map area instead.
    if (useRefreshLayout && locationSelection) {
      setLocationSelection(null);
      setMapAreaSearched(true);
    }
    setCameraSearch('due');
    debouncedSetSearchAreaFromViewport(e.target);
  };

  const handleBedroomsChange = ({
    minBeds: nextMinBeds,
    maxBeds: nextMaxBeds,
  }: BedroomFilterValue) => {
    searchState.set.setBedrooms(Number(nextMinBeds));
    searchState.set.setMaxBedrooms(Number(nextMaxBeds));
  };
  const handleSetBathrooms = (value: string) => searchState.set.setBathrooms(Number(value));
  const handleMinPriceChange = (value: number | null) => searchState.set.setMinPrice(value);
  const handleMaxPriceChange = (value: number | null) => searchState.set.setMaxPrice(value);
  const handleSingleFamilyChange = (_: string, checked: string | boolean) =>
    searchState.set.setSingleFamily(!!checked);
  const handleCondoChange = (_: string, checked: string | boolean) =>
    searchState.set.setCondo(!!checked);
  const handleTownhomeChange = (_: string, checked: string | boolean) =>
    searchState.set.setTownhome(!!checked);
  const handleNoHoasChange = (_: string, checked: string | boolean) =>
    searchState.set.setNoHoas(!!checked);
  const handleSingleStoryChange = (_: string, checked: string | boolean) =>
    searchState.set.setSingleStory(!!checked);
  const handlePoolChange = (_: string, checked: string | boolean) =>
    searchState.set.setMustHavePool(!!checked);
  const handleMinYearBuiltChange = (value: string) =>
    searchState.set.setMinYearBuilt(Number(value));
  const handleMaxYearBuiltChange = (value: string) =>
    searchState.set.setMaxYearBuilt(Number(value));
  const handleMinLotSizeChange = (value: string) => searchState.set.setMinLotSize(Number(value));
  const handleMaxLotSizeChange = (value: string) => searchState.set.setMaxLotSize(Number(value));
  const handleMinSquareFootageChange = (min: string) =>
    searchState.set.setMinSquareFootage(Number(min));
  const handleMaxSquareFootageChange = (max: string) =>
    searchState.set.setMaxSquareFootage(Number(max));

  const handleShowList = () => {
    setShowList(true);
  };
  const handleShowMap = () => setShowList(false);
  const onFiltersButtonClick = () => {
    closePropertyDetails();
    setShowList(false);
    setShowPdp(false);
    setHasOpenedFilters(true);
    setShowFilters(true);
  };

  const handleLocationSelectionChange = (selection: LocationSearchSelection | null) => {
    cancelLocationSearch();
    setLocationSelection(selection);
    if (selection) setMapAreaSearched(false);
    setSelectedLocation(undefined);
    if (!selection) {
      return;
    }
    setIsMobileHeaderFocused(false);
    if (selection.kind === 'location') {
      abandonClusterSearch();
      cancelMapFilterChanges();
      const changeId = mapFilterChangeIdRef.current;
      mapRef.current?.stop();
      cancelViewportSearch();
      const { location } = selection;
      const map = mapRef.current;
      const coordinates = location.coordinates;
      if (map && coordinates) {
        cancelLocationMoveRef.current = moveToSearchLocation({
          map,
          location: { ...location, coordinates },
          onComplete: () => {
            // A state's viewport also covers homes in neighbouring states.
            if (location.zoneType === 'STATE') {
              pendingSearchTriggerRef.current = PropertySearchTrigger.LOCATION_SELECT;
              void applyZoneSearch(location);
              return;
            }
            debouncedSetSearchAreaFromLocation(
              map,
              PropertySearchTrigger.LOCATION_SELECT,
              location,
            );
            if (useRefreshLayout) debouncedSetSearchAreaFromLocation.flush();
          },
        });
        return;
      }
      if (isUuid(location.value)) {
        pendingSearchTriggerRef.current = PropertySearchTrigger.LOCATION_SELECT;
      }
      void applyZoneSearch(location).then(() => {
        if (useRefreshLayout && changeId === mapFilterChangeIdRef.current) {
          searchState.applyCanonical(parseQueryParamsFromSearch(window.location.search));
        }
      });
      return;
    }
    if (usePropertyDialog) {
      openMapProperty({
        addressId: selection.address.addressId,
        origin: null,
        photo: null,
        preview: null,
        coordinates: null,
      });
    } else {
      handleFetchPropertyDetails(selection.address.addressId);
    }
    // Address selection previews a listing without changing the browse area.
    trackJourneyEvent(
      'marketplaceProperty',
      'propertySearch',
      buildPropertySearchEvent({
        trigger: PropertySearchTrigger.LOCATION_SELECT,
        filters: { minBeds, minBaths, minPrice, maxPrice },
        market: marketInfo.name,
        resultsCount: listings?.totalCount,
        sort: searchState.get.sort,
      }),
    );
  };

  const handleOnMobileFilterClick = () => {
    closePropertyDetails();
    setShowList(false);
    setHasOpenedFilters(true);
    setShowFilters(true);
  };

  const [isMobileHeaderFocused, setIsMobileHeaderFocused] = useState(false);
  const [hoveredListingAddressId, setHoveredListingAddressId] = useState<string | null>(null);

  const handleOnMobileHeaderFocus = () => {
    setIsMobileHeaderFocused(true);
  };

  const handleOnMobileHeaderBlur = () => {
    setIsMobileHeaderFocused(false);
  };

  // A group of one home is that home: it draws as a price pin, never as a bubble.
  const singleHomeClusters = useMemo(
    () => (visibleMapClusters ?? []).filter((cluster) => cluster.listingCount === 1),
    [visibleMapClusters],
  );
  const refreshPins = useMemo<MapPin[]>(() => {
    if (!useRefreshLayout) return [];
    const markers = [
      ...(visibleMapMarkers ?? []),
      ...singleHomeClusters.flatMap((cluster) => (cluster.listing ? [cluster.listing] : [])),
    ];
    const seen = new Set<string>();
    return markers.flatMap((marker) => {
      if (seen.has(marker.addressId)) return [];
      seen.add(marker.addressId);
      return [
        {
          addressId: marker.addressId,
          latitude: marker.location.latitude,
          longitude: marker.location.longitude,
          listPriceCents: marker.listPriceCents,
          isLiked: likedPropertyIds?.has(marker.addressId) ?? false,
        },
      ];
    });
  }, [useRefreshLayout, visibleMapMarkers, singleHomeClusters, likedPropertyIds]);
  const refreshClusters = useMemo<MapClusterPin[]>(
    () =>
      useRefreshLayout
        ? (visibleMapClusters ?? [])
            .filter((cluster) => cluster.listingCount !== 1 || !cluster.listing)
            .map((cluster) => ({
              id: cluster.id,
              latitude: cluster.location.latitude,
              longitude: cluster.location.longitude,
              count: cluster.listingCount,
              expansionZoom: cluster.clusterExpansionZoom,
              envelope: cluster.envelope,
            }))
        : [],
    [useRefreshLayout, visibleMapClusters],
  );
  const openMapProperty = useCallback(
    (selection: MapPropertySelection) => {
      if (selection.addressId === selectedAddressId) return;
      cancelLocationSearch();
      cancelViewportSearch();
      setShowFilters(false);
      const image = selection.origin?.querySelector<HTMLImageElement>('[aria-hidden="false"] img');
      setMapPropertySelection({
        ...selection,
        photo: selection.photo
          ? { ...selection.photo, url: image?.currentSrc || selection.photo.url }
          : null,
      });
      setSelectedAddressId(selection.addressId);
      const params = buildAddressPreviewParams(selection.addressId);
      pushUrl(`${window.location.pathname}?${params}`);
    },
    [
      selectedAddressId,
      cancelLocationSearch,
      cancelViewportSearch,
      setShowFilters,
      setMapPropertySelection,
      setSelectedAddressId,
    ],
  );
  const findMapPinListing = useCallback(
    (addressId: string) => {
      const listing = listings?.listings.find((item) => item.addressId === addressId);
      return listing ? listingCardData(listing, likedPropertyIds) : null;
    },
    [listings, likedPropertyIds],
  );
  const findMapPinPreview = useCallback(
    (addressId: string): PinPreviewListing | null => {
      const listing = findMapPinListing(addressId);
      if (listing) {
        return {
          source: 'results',
          listing,
          photoUrl: loadedListingCardPhoto(addressId)?.url ?? null,
        };
      }
      const marker = visibleMapMarkers?.find((item) => item.addressId === addressId);
      return marker ? markerPinPreviewListing(marker) : null;
    },
    [findMapPinListing, visibleMapMarkers],
  );
  const openMapPin = useCallback(
    (pin: MapPin, origin: HTMLElement) => {
      const preview = findMapPinListing(pin.addressId);
      const firstPhoto = preview?.photos[0];
      openMapProperty({
        addressId: pin.addressId,
        origin,
        photo:
          loadedListingCardPhoto(pin.addressId) ??
          (firstPhoto ? { url: firstPhoto, index: 0 } : null),
        preview,
        coordinates: [pin.longitude, pin.latitude],
        instant: true,
      });
    },
    [openMapProperty, findMapPinListing],
  );
  const openMapListing = useCallback(
    (listing: ListingCardData, origin: HTMLElement, photo?: ListingPreviewPhoto) => {
      openMapProperty({
        addressId: listing.addressToken,
        origin,
        photo: photo ?? null,
        preview: listing,
        mapZoom: PROPERTY_FOCUS_MAPBOX_ZOOM,
        coordinates:
          listing.longitude != null && listing.latitude != null
            ? [listing.longitude, listing.latitude]
            : null,
      });
    },
    [openMapProperty],
  );

  const priceBands = usePriceDistribution(useRefreshLayout ? appliedListInput : undefined);
  const draftFilters = searchState.getCanonical();
  // The sheet edits a draft of its sections; Show applies it, closing discards it.
  const [filtersSheetDraft, setFiltersSheetDraft] = useState<MapFiltersValue | null>(null);
  const [filtersSheetCleared, setFiltersSheetCleared] = useState(false);
  // What the sheet's pills apply against: the search it opened with, or nothing once cleared.
  const sheetOpenedWith = filtersSheetCleared
    ? clearedSheetValue()
    : sheetValueFromQuery(draftFilters);
  const filtersSheetOpen = filtersSheetDraft != null;
  const [filtersSheetOrigin, setFiltersSheetOrigin] = useState<HTMLElement | null>(null);
  const handleOpenMapFilters = useCallback(
    (origin: HTMLElement | null) => {
      setFiltersSheetOrigin(origin);
      setFiltersSheetCleared(false);
      setFiltersSheetDraft(sheetValueFromQuery(searchState.getCanonical()));
    },
    [searchState],
  );
  const changeFiltersSheetDraft = (patch: (draft: MapFiltersValue) => MapFiltersValue) =>
    setFiltersSheetDraft((draft) => (draft ? patch(draft) : draft));
  const changeMapFilters = (override: NonNullable<Parameters<typeof applyFilters>[0]>) => {
    const changeId = ++mapFilterChangeIdRef.current;
    searchState.applyCanonical({ ...searchState.getCanonical(), ...override });
    void applyMapFilterChange(override, changeId);
  };
  const handleMapHomeTypeChange: MapFiltersSheetProps['onHomeTypeChange'] = (type, on) =>
    changeFiltersSheetDraft((draft) => ({
      ...draft,
      homeTypes: { ...draft.homeTypes, [type]: on },
    }));
  const handleMapFeatureChange: MapFiltersSheetProps['onFeatureChange'] = (feature, on) =>
    changeFiltersSheetDraft((draft) => ({
      ...draft,
      features: { ...draft.features, [feature]: on },
    }));
  const handleMapLotChange: MapFiltersSheetProps['onLotChange'] = (lot) =>
    changeFiltersSheetDraft((draft) => ({ ...draft, lot }));
  const handleMapBedsChange: MapFilterBarProps['onBedsChange'] = (next) =>
    changeMapFilters({ bedrooms: next, maxBedrooms: null });
  const handleMapBathsChange: MapFilterBarProps['onBathsChange'] = (next) =>
    changeMapFilters({ bathrooms: next });
  const handleMapPriceCommit = () => applyPriceSoon.flush();
  const handleMapPriceChange = (next: PriceRangeValue) => {
    const changeId = ++mapFilterChangeIdRef.current;
    searchState.set.setMinPrice(next.min);
    searchState.set.setMaxPrice(next.max);
    applyPriceSoon(next, changeId);
  };
  const handleMapSizeChange: MapFiltersSheetProps['onSizeChange'] = (size) =>
    changeFiltersSheetDraft((draft) => ({ ...draft, size }));
  const handleMapStatusChange: MapFiltersSheetProps['onStatusChange'] = (status, on) =>
    changeFiltersSheetDraft((draft) => ({ ...draft, status: { ...draft.status, [status]: on } }));
  const handleClearMapFilters = () => {
    setFiltersSheetCleared(true);
    setFiltersSheetDraft(clearedSheetValue());
  };
  const handleApplyMapFilters = () => {
    if (!filtersSheetDraft) return;
    const override = sheetQueryOverride(filtersSheetDraft, sheetOpenedWith);
    if (!filtersSheetCleared) {
      changeMapFilters(override);
      return;
    }
    // Clear filters resets the whole search, beds, baths, price and year built
    // included; the pills chosen after it apply on top of the reset.
    clearActiveSavedSearch();
    pendingSearchTriggerRef.current = PropertySearchTrigger.RESET;
    changeMapFilters({
      ...searchState.resetQuery,
      availableSoon: false,
      includeAvailableSoon: false,
      ...override,
    });
  };
  const handleCloseMapFilters = () => {
    setFiltersSheetDraft(null);
    setFiltersSheetCleared(false);
  };
  const handleMapSavedSearch = (savedSearch: Parameters<typeof applySavedSearch>[0]) => {
    // The phone sheet stays where it is; the count it shows is the focused heading.
    void handleApplySavedSearch(savedSearch).then(() =>
      resultsFocusRef.current?.focus({ preventScroll: true }),
    );
  };
  const handleSavedSearchesLogin = () => {
    cancelMapFilterChanges();
    const redirectTo = `${window.location.pathname}${window.location.search}`;
    window.location.assign(`/login?redirectTo=${encodeURIComponent(redirectTo)}`);
  };
  const handleOpenFiltersFromSavedSearches = () => handleOpenMapFilters(null);
  // Only the refresh layout sends the status filters, so only there do they block saving.
  const hasUnsupportedSavedSearchStatus = hasUnsavableFilters(draftFilters, {
    statusFilters: useRefreshLayout,
  });
  const refreshSavedSearches = {
    onApplySavedSearch: handleMapSavedSearch,
    activeSavedSearchId: activeSavedSearch?.id,
    onLogin: handleSavedSearchesLogin,
    onOpenFilters: handleOpenFiltersFromSavedSearches,
  };
  const sheetFilters = sheetValueFromQuery(draftFilters);
  // The Show button counts the draft inside the area the results already cover.
  const draftResultCount = useDraftResultCount(
    filtersSheetDraft && appliedListInput
      ? {
          ...appliedListInput,
          filters: searchState.buildSearchPayload(
            sheetQueryOverride(filtersSheetDraft, sheetOpenedWith),
          ).listInput.filters,
        }
      : undefined,
  );
  const refreshFiltersSheet =
    useRefreshLayout && filtersSheetDraft ? (
      <MapFiltersSheet
        mobile={!isDesktopClient}
        value={filtersSheetDraft}
        resultCount={draftResultCount}
        onClose={handleCloseMapFilters}
        onApply={handleApplyMapFilters}
        onHomeTypeChange={handleMapHomeTypeChange}
        onFeatureChange={handleMapFeatureChange}
        onSizeChange={handleMapSizeChange}
        onLotChange={handleMapLotChange}
        onStatusChange={handleMapStatusChange}
        onClearAll={handleClearMapFilters}
        showComingSoon={showInventorySegment}
        origin={filtersSheetOrigin}
      />
    ) : null;
  const refreshFilterBar = useRefreshLayout ? (
    <MapFilterBar
      allFiltersCount={countAppliedFilters(sheetFilters)}
      trailing={
        <SaveSearchButton
          appearance="chip"
          query={
            applyingSavedSearch ? toPropertySearchQuery(applyingSavedSearch) : getSaveSearchQuery()
          }
          activeSavedSearch={applyingSavedSearch ?? activeSavedSearch}
          locationLabel={locationLabel}
          onSaved={markActiveSavedSearch}
          disabled={!applyingSavedSearch && hasUnsupportedSavedSearchStatus}
          disabledReason="Clear status filters before saving this search."
          searchIsSet={
            Boolean(locationSelection) ||
            mapAreaSearched ||
            countAppliedFilters(sheetFilters) > 0 ||
            Boolean(minBeds || maxBeds || minBaths || minPrice || maxPrice)
          }
          refetchSavedSearchesOnMount={false}
        />
      }
      allFiltersOpen={filtersSheetOpen}
      onAllFilters={handleOpenMapFilters}
      beds={draftFilters.bedrooms ?? 0}
      maxBedrooms={draftFilters.maxBedrooms}
      baths={draftFilters.bathrooms ?? 0}
      price={{
        min: draftFilters.minListPrice ?? null,
        max: draftFilters.maxListPrice ?? null,
      }}
      priceBands={priceBands}
      onBedsChange={handleMapBedsChange}
      onBathsChange={handleMapBathsChange}
      onPriceChange={handleMapPriceChange}
      onPriceCommit={handleMapPriceCommit}
    />
  ) : null;
  const handleMapNearby = ({ latitude, longitude }: { latitude: number; longitude: number }) => {
    const map = mapRef.current;
    if (!map) return false;
    cancelLocationSearch();
    setLocationSelection(null);
    setMapAreaSearched(false);
    map.flyTo({ center: [longitude, latitude], zoom: 12 });
    return true;
  };
  // The show-my-location button moves the map as "Find homes nearby" does.
  const [locating, setLocating] = useState(false);
  const handleLocate = () => {
    if (locating) return;
    setLocating(true);
    void locateVisitor().then((visitor) => {
      setLocating(false);
      if (visitor) handleMapNearby(visitor);
    });
  };
  const refreshSearchBar = useRefreshLayout ? (
    <MapRefreshSearchBar
      selection={locationSelection}
      mapAreaSearched={mapAreaSearched}
      onMapAreaClear={() => setMapAreaSearched(false)}
      onSelectionChange={handleLocationSelectionChange}
      onNearbyStart={handleNearbyStart}
      onNearby={handleMapNearby}
      onSearch={handleApplyFilters}
      searchDisabled={Boolean(errors && Object.keys(errors).length > 0)}
      savedSearches={refreshSavedSearches}
    />
  ) : null;

  const RenderBody = (
    <MarketplaceMapLayout
      hideFooter
      headerContentClassName={mapRefreshEnabled ? 'max-w-none px-3 md:px-10' : undefined}
    >
      <Toasty>
        <Col className="relative h-full w-full overflow-hidden border-t border-od-border-primary bg-od-bg-primary md:flex-row md:[--inventory-width:max(40%,390px)] lg:[--inventory-width:42%] xl:[--inventory-width:46%]">
          {/** Header */}
          <Row
            onBlur={handleOnMobileHeaderBlur}
            onFocus={handleOnMobileHeaderFocus}
            className={cn(
              'fixed top-[calc(var(--spacing)*1.25+var(--site-promo-banner-height,0px))] h-15 items-center justify-center transition-[width,left] duration-[250ms] md:hidden',
              useRefreshLayout && 'hidden',
              usePropertyDialog && selectedAddressId ? 'z-[2000]' : 'z-[8000]',
              isMobileHeaderFocused
                ? 'left-2.5 w-[95%]'
                : 'left-13.75 w-[calc(100%_-_var(--spacing)*50)]',
            )}
          >
            <Col className="w-full">
              <MobileHeaderFilters
                selection={locationSelection}
                onSelectionChange={handleLocationSelectionChange}
                isFocused={isMobileHeaderFocused}
                onBack={handleOnMobileHeaderBlur}
                onOpenFilters={handleOnMobileFilterClick}
              />
            </Col>
          </Row>
          {useRefreshLayout ? (
            <HomesMapRefresh
              mobile={!isDesktopClient}
              marketShape={initialMarketInfo.shape}
              mapRef={mapRef}
              onMapClick={closePropertyDetails}
              onMapReady={handleMapReady}
              onMapMoveEnd={handleOnMoveEnd}
              pins={refreshPins}
              focusedAddressId={focusedAddressId}
              clusters={refreshClusters}
              hoveredAddressId={hoveredListingAddressId}
              showMap={showRefreshMap}
              startArea={startArea}
              onLocate={handleLocate}
              locating={locating}
              onGroupingChange={setGroupMapPins}
              searchInput={appliedListInput}
              resultsFocusRef={resultsFocusRef}
              onListingClick={openMapPin}
              findPinPreview={findMapPinPreview}
              onClusterZoom={handleClusterZoom}
              searchBar={refreshSearchBar}
              filterBar={
                <>
                  {refreshFilterBar}
                  {refreshFiltersSheet}
                </>
              }
              mobileSortControl={
                <ListingSortControl
                  analyticsName="marketplace-sort-mobile-refresh"
                  value={appliedSort}
                  onChange={handleSortChange}
                />
              }
              homesCount={listings?.totalCount}
              isRefreshing={isLoading && listings != null}
              cameraSearchPending={cameraSearchPending}
            >
              <PropertyListRefreshOverlay isRefreshing={isLoading && listings != null}>
                <PropertyList
                  cardVariant="map-refresh"
                  isLoading={isLoading}
                  listings={listings}
                  likedPropertyIds={likedPropertyIds}
                  initialFavoritesPending={initialFavoritesPending}
                  onFavoriteChange={onFavoriteChange}
                  onListingHover={setHoveredListingAddressId}
                  onListingOpen={openMapListing}
                  firstPhotoLoadMode="priority"
                  pagination={undefined}
                  infiniteScroll={{
                    hasMore,
                    prefetchAfter,
                    isLoading: isLoadingMore,
                    error: loadMoreError,
                    onLoadMore: loadMore,
                    // The first search around the visitor replaces the list, so it loads no
                    // more homes until the map opens.
                    paused: isLoading || Boolean(selectedAddressId) || startArea === 'pending',
                  }}
                />
              </PropertyListRefreshOverlay>
            </HomesMapRefresh>
          ) : (
            <Col
              aria-hidden={
                (isSmallerThanMD && (showList || showFilters)) ||
                (!isSmallerThanMD && isSmallerThanLG && showFilters)
              }
              className={cn(
                'relative h-[calc(100vh-var(--site-promo-banner-height,0px))] w-full touch-none items-center border-t border-od-border-primary transition-[opacity,visibility] duration-500 md:h-full md:min-w-0 md:w-[calc(100%_-_var(--inventory-width))] md:shrink-0',
                (showList || showFilters) && 'max-md:invisible max-md:opacity-0',
                showFilters && 'md:invisible md:opacity-0 lg:visible lg:opacity-100',
              )}
            >
              {initialPropertyPage?.property.latitude != null &&
              initialPropertyPage.property.longitude != null ? (
                <StaticPropertyMap
                  latitude={initialPropertyPage.property.latitude}
                  longitude={initialPropertyPage.property.longitude}
                />
              ) : (
                !mapRefreshEnabled && (
                  <MarketplaceMap
                    mapRef={mapRef}
                    onClick={closePropertyDetails}
                    onMoveEnd={handleOnMoveEnd}
                    marketShape={initialMarketInfo.shape}
                  >
                    <MarketplaceMapMarkers
                      markers={mapMarkers}
                      likedPropertyIds={likedPropertyIds}
                      selectedAddressId={propertyDetails?.addressToken}
                      hoveredListingAddressId={hoveredListingAddressId}
                      loadingAddressId={loadingAddressId}
                      onListingClick={handleFetchPropertyDetails}
                    />
                  </MarketplaceMap>
                )
              )}
              <Button
                analyticsName="list-view"
                aria-label="List"
                onClick={handleShowList}
                size="lg"
                variant="secondaryInverse"
                className={cn(
                  'fixed bottom-7.5 left-1/2 z-2 -translate-x-1/2 md:hidden',
                  showList && 'hidden',
                )}
              >
                List
              </Button>
            </Col>
          )}
          <Col
            id="property-list-container"
            as="section"
            aria-label="Homes inventory"
            aria-hidden={useRefreshLayout || (isSmallerThanMD ? !showList : showPdp || showFilters)}
            className={cn(
              'absolute z-3 h-full w-full min-w-0 shrink-0 bg-od-bg-primary max-md:items-center max-md:overflow-y-scroll max-md:px-5 max-md:pb-32.5 md:relative md:z-auto md:w-[var(--inventory-width)] md:border-t md:border-od-border-primary md:transition-[opacity,visibility] md:duration-500',
              SLIDE_PANEL_TRANSITION_CLASS,
              useRefreshLayout && 'hidden',
              !showList && 'max-md:invisible max-md:translate-y-[100vh] max-md:opacity-0',
              (showPdp || showFilters) && 'md:invisible md:opacity-0',
            )}
          >
            <Col className="z-1 h-full w-full max-md:px-6 md:overflow-y-scroll">
              {!isSmallerThanMD && (
                <QuickFilters
                  selection={locationSelection}
                  onSelectionChange={handleLocationSelectionChange}
                  bedrooms={String(minBeds)}
                  maxBedrooms={String(maxBeds)}
                  bathrooms={String(minBaths)}
                  minPrice={minPrice}
                  maxPrice={maxPrice}
                  onFilterChange={handleQuickFilterChange}
                  onPriceChange={handleQuickPriceChange}
                  onFiltersButtonClick={onFiltersButtonClick}
                  isFocused={isMobileHeaderFocused}
                  inventoryControl={desktopInventoryControl}
                />
              )}
              <Col className="max-md:flex-wrap max-md:justify-between max-md:pb-13 md:px-5 lg:px-6">
                {isLoggedIn && !isSmallerThanMD ? (
                  <Row className="py-4">
                    <SavedSearchesDropdown
                      onApplySavedSearch={handleApplySavedSearch}
                      activeSavedSearchId={activeSavedSearch?.id}
                      onOpenFilters={onFiltersButtonClick}
                      size="sm"
                    />
                  </Row>
                ) : null}
                {isSmallerThanMD ? (
                  <>
                    <Row className="items-center justify-between gap-4 py-6">
                      <Text
                        as="p"
                        aria-busy={(isLoading && listings != null) || undefined}
                        className="text-body-sm"
                      >
                        {listings?.totalCount ?? 0} homes
                      </Text>
                      <ListingSortControl
                        analyticsName="marketplace-sort-mobile"
                        className="shrink-0"
                        value={appliedSort}
                        onChange={handleSortChange}
                      />
                    </Row>
                    {showInventorySegment ? (
                      <Row className="justify-center pb-4">{mobileInventoryControl}</Row>
                    ) : null}
                  </>
                ) : (
                  <Row
                    className={cn('items-center justify-between gap-4 pb-6', !isLoggedIn && 'pt-4')}
                  >
                    <Text
                      as="p"
                      aria-busy={(isLoading && listings != null) || undefined}
                      className="min-w-0 text-body-sm"
                    >
                      {listings?.totalCount ?? 0}{' '}
                      {appliedInventoryHeading(appliedAvailableSoon, appliedIncludeAvailableSoon)}{' '}
                      near {locationLabel}
                    </Text>
                    <DesktopListingSortControl
                      analyticsName="marketplace-sort-desktop"
                      className="shrink-0"
                      value={appliedSort}
                      onChange={handleSortChange}
                    />
                  </Row>
                )}
                {appliedAvailableSoon || appliedIncludeAvailableSoon ? (
                  <Text as="p" className="pb-4 text-body-sm text-od-content-tertiary md:pb-6">
                    {AVAILABLE_SOON_DISCLOSURE}
                  </Text>
                ) : null}
                <PropertyListRefreshOverlay isRefreshing={isLoading && listings != null}>
                  <PropertyList
                    cardVariant={isSmallerThanMD ? undefined : 'search-result'}
                    isLoading={isLoading}
                    listings={listings}
                    likedPropertyIds={likedPropertyIds}
                    initialFavoritesPending={initialFavoritesPending}
                    onFavoriteChange={onFavoriteChange}
                    onListingHover={setHoveredListingAddressId}
                    firstPhotoLoadMode={isSmallerThanMD ? 'deferred' : 'priority'}
                    pagination={pagination}
                  />
                </PropertyListRefreshOverlay>
                <PopularMarkets
                  markets={activeMarkets}
                  sellMarket={sellMarket}
                  className="pt-12 pb-27.5 md:pb-25"
                />
              </Col>
            </Col>
            <Button
              analyticsName="list-view"
              aria-label="Map"
              onClick={handleShowMap}
              size="lg"
              variant="secondaryInverse"
              className={cn(
                'fixed bottom-7.5 left-1/2 z-2 -translate-x-1/2 md:hidden',
                !showList && 'hidden',
              )}
            >
              Map
            </Button>
          </Col>
          <Col
            id="property-filters-container"
            aria-hidden={!showFilters || useRefreshLayout}
            aria-label="Search filters"
            as="aside"
            className={cn(
              'fixed -top-1.25 z-[15000] h-full w-full items-center bg-od-bg-primary lg:absolute lg:right-0 lg:top-0 lg:z-2 lg:w-[var(--inventory-width)] lg:border-t lg:border-od-border-primary',
              SLIDE_PANEL_TRANSITION_CLASS,
              useRefreshLayout && 'hidden',
              !showFilters &&
                'invisible -translate-y-[100vh] opacity-0 lg:translate-x-[50vh] lg:translate-y-0',
            )}
          >
            {hasOpenedFilters && (
              <MapSearchFilters
                minBeds={toFilterString(minBeds)}
                maxBeds={toFilterString(maxBeds)}
                minBaths={toFilterString(minBaths)}
                minPrice={minPrice}
                maxPrice={maxPrice}
                singleFamily={singleFamily}
                condo={condo}
                townhome={townhome}
                noHoas={noHoas}
                singleStory={singleStory}
                mustHavePool={mustHavePool}
                minYearBuilt={toFilterString(minYearBuilt)}
                maxYearBuilt={toFilterString(maxYearBuilt)}
                minLotSize={toFilterString(minLotSize)}
                maxLotSize={toFilterString(maxLotSize)}
                minSquareFootage={toFilterString(minSquareFootage)}
                maxSquareFootage={toFilterString(maxSquareFootage)}
                handleError={handleError}
                handleBedroomsChange={handleBedroomsChange}
                handleMinBathsChange={handleSetBathrooms}
                handleMinSquareFootageChange={handleMinSquareFootageChange}
                handleMaxSquareFootageChange={handleMaxSquareFootageChange}
                handleMinPriceChange={handleMinPriceChange}
                handleMaxPriceChange={handleMaxPriceChange}
                handleSingleFamilyChange={handleSingleFamilyChange}
                handleCondoChange={handleCondoChange}
                handleTownhomeChange={handleTownhomeChange}
                handleNoHoasChange={handleNoHoasChange}
                handleSingleStoryChange={handleSingleStoryChange}
                handlePoolChange={handlePoolChange}
                handleMinYearBuiltChange={handleMinYearBuiltChange}
                handleMaxYearBuiltChange={handleMaxYearBuiltChange}
                handleMinLotSizeChange={handleMinLotSizeChange}
                handleMaxLotSizeChange={handleMaxLotSizeChange}
                onApplyFilters={isSmallerThanLG ? handleMobileFilterClick : handleApplyFilters}
                onResetFilters={handleResetFilters}
                onClose={handleCloseFilters}
                isLoading={isLoading}
                errors={errors}
                saveSearchQuery={hasUnsupportedSavedSearchStatus ? undefined : getSaveSearchQuery()}
                onApplySavedSearch={handleApplySavedSearch}
                activeSavedSearch={activeSavedSearch}
                locationLabel={selectedLocation?.label}
                onSavedSearchCreated={markActiveSavedSearch}
              />
            )}
          </Col>
          {usePropertyDialog && selectedAddressId && (
            <MapPropertyDialog
              initialPropertyPage={initialPropertyPage}
              fallbackFocusRef={resultsFocusRef}
              onMapMoveEnd={handleOnMoveEnd}
              key={selectedAddressId}
              selection={
                mapPropertySelection?.addressId === selectedAddressId
                  ? mapPropertySelection
                  : {
                      addressId: selectedAddressId,
                      origin: null,
                      photo: null,
                      preview: null,
                      coordinates: null,
                    }
              }
              mapRef={mapRef}
              onClose={closePropertyDetails}
              onFavoriteChange={onFavoriteChange}
              initialIsLiked={likedPropertyIds?.has(selectedAddressId)}
              initialFavoritePending={initialFavoritesPending}
            />
          )}
          {/** Mini PDP (Medium/Large) */}
          <Col
            aria-hidden={!showPdp}
            className={cn(
              'absolute right-0 z-2 h-full w-[var(--inventory-width)] overflow-y-scroll bg-od-bg-primary max-md:hidden',
              SLIDE_PANEL_TRANSITION_CLASS,
              !showPdp && 'invisible translate-x-[50vh] opacity-0',
            )}
          >
            {!isSmallerThanMD && propertyDetailsWithFavorite && (
              <MiniPropertyDetailPage
                property={propertyDetailsWithFavorite}
                onClose={closePropertyDetails}
                key={propertyDetailsWithFavorite.addressToken}
              />
            )}
          </Col>
          {/** Mobile Mini PDP (Small) */}
          <Col
            // oxlint-disable-next-line react/refs -- react19-baseline: pre-existing, do not copy. Read refs in handlers or effects, never during render
            ref={mobileMiniPdpSwipe.panelRef}
            aria-hidden={!showPdp}
            className={cn(
              'absolute bottom-0 left-2.5 right-2.5 z-[3000] overflow-hidden rounded-t-4xl bg-od-bg-primary md:hidden',
              MOBILE_MINI_PDP_PANEL_HEIGHT_CLASS,
              MOBILE_MINI_PDP_PANEL_TRANSITION_CLASS,
              !showPdp && cn('invisible opacity-0', MOBILE_MINI_PDP_PANEL_CLOSED_TRANSLATE_CLASS),
              // oxlint-disable-next-line react/refs -- react19-baseline: pre-existing, do not copy. Read refs in handlers or effects, never during render
              mobileMiniPdpSwipe.panelClassName,
            )}
          >
            {isSmallerThanMD && propertyDetails && (
              <MobileMiniPropertyDetailPage
                property={propertyDetails}
                onClose={closePropertyDetails}
              />
            )}
          </Col>
        </Col>
        <SearchErrorToast errorId={errorId} clearError={clearError} />
      </Toasty>
    </MarketplaceMapLayout>
  );

  return (
    <>
      {initialPropertyPage ? (
        <PropertySeoHead property={initialPropertyPage.property} />
      ) : (
        <MarketplaceSeoHead
          marketInfo={marketInfo}
          activeMarkets={activeMarkets}
          canonicalPath={
            searchPathname === initialSearchPathname
              ? (canonicalOverride ?? searchPathname)
              : searchPathname
          }
          listingCount={searchPathname === initialSearchPathname ? ssrListingCount : null}
          heroPhotoUrl={pickHomesMarketHeroPhoto(
            listings?.listings.flatMap((listing) => listing.photoUrls ?? []) ?? [],
          )}
        />
      )}
      {RenderBody}
    </>
  );
};

const NOT_FOUND = { notFound: true } as const;

// Map of query params to their canonical path overrides
const CANONICAL_PATH_OVERRIDES: Record<string, string> = {
  is_near_me_page: '/homes-for-sale-near-me',
};

/**
 * `parseQueryParams` drops `ssid`, so a page link built from parsed params loses
 * it. Re-appending keeps the anchor's address equal to the one a click pushes,
 * and keeps a reload on that link restoring the active saved search.
 */
const appendSavedSearchId = (path: string, savedSearchId: string | null): string => {
  if (savedSearchId == null || savedSearchId === '') {
    return path;
  }
  const separator = path.includes('?') ? '&' : '?';
  return `${path}${separator}${SAVED_SEARCH_ID_PARAM}=${encodeURIComponent(savedSearchId)}`;
};

const MapPropertyDialog = dynamic(
  () =>
    import('components/marketplace/map/refresh/MapPropertyDialog').then(
      (module) => module.MapPropertyDialog,
    ),
  { ssr: false },
);

export const buildCanonicalSearchPath = (
  pathname: string,
  query: PropertySearchQuery,
  options?: { seedFromUrl?: boolean },
): string => {
  const params = buildQueryParams({ ...query, market: null }, options);
  const queryString = params.toString();
  return `${pathname}${queryString ? `?${queryString}` : ''}`;
};

export const getServerSideProps: CacheSafeGetServerSideProps<{
  dehydratedState: DehydratedState;
  initialVariables: MarketplaceBrowseVariables;
  initialSearchPathname: string;
  marketInfo: MarketInfo;
  queryParams: PropertySearchQuery;
  activeMarkets: ActiveMarket[];
  canonicalOverride: string | null;
  savedSearchId: string | null;
  initialZoneIds: string[] | null;
  initialViewportAreaName: string | null;
  ssrListingCount: number | null;
  mapRefreshEnabled: boolean;
  stagedPhotosExposure: StagedPhotosExposureData | null;
}> = async (context) => {
  const queryParams = parseQueryParams(context.query);
  const savedSearchId = parseSavedSearchId(context.query);
  const route = parseMarketParam(queryParams.market);
  // Match SSR search precedence (vertices > zoneIds): a zid route that also
  // carries a viewport polygon must not seed routeZoneIds, or the controller
  // would force vertices to null on the next client search/save.
  const hasViewportPolygon = (queryParams.vertices?.length ?? 0) > 0;
  let initialZoneIds: string[] | null = null;
  if (!hasViewportPolygon) {
    initialZoneIds = route.kind === 'zone' ? [route.zoneId] : (queryParams.zoneIds ?? null);
  }

  // Check for canonical path overrides based on query params from cloudflare routing
  const overrideKey = Object.keys(CANONICAL_PATH_OVERRIDES).find(
    (key) => context.query[key] === 'true',
  );
  const canonicalOverride = overrideKey ? CANONICAL_PATH_OVERRIDES[overrideKey] : null;

  let activeMarketsPromise: Promise<ActiveMarket[]> = Promise.resolve([]);
  let marketInfo: MarketInfo | false = false;
  let resolvedMarket: string | null = null;

  if (route.kind === 'market') {
    const { market } = route;
    resolvedMarket = market.toLowerCase();
    activeMarketsPromise = fetchActiveBuyMarkets(context.req.headers);
    const routeData = await fetchMarketRouteData(market, context.req.headers);
    const requestedMarket = routeData.marketplace.market;
    const skipMarketFallback = context.query.skip_market_fallback === 'true';
    if (
      (!requestedMarket || !requestedMarket.active) &&
      !skipMarketFallback &&
      market.toLowerCase() !== DEFAULT_MARKET
    ) {
      return {
        redirect: {
          destination: `/homes/${DEFAULT_MARKET}`,
          permanent: false,
        },
      };
    }
    if (!requestedMarket) {
      return NOT_FOUND;
    }
    resolvedMarket = requestedMarket.identifier;
    marketInfo = buildMarketInfoFromMarket(requestedMarket, queryParams);
  } else if (route.kind === 'zone') {
    activeMarketsPromise = fetchActiveBuyMarkets(context.req.headers);
    const routeData = await fetchZoneRouteData(route.zoneId, context.req.headers);
    if (!routeData.marketplace.zone) {
      return NOT_FOUND;
    }
    marketInfo = buildMarketInfoFromZone(routeData.marketplace.zone, queryParams);
  } else {
    return NOT_FOUND;
  }

  if (!marketInfo) {
    return NOT_FOUND;
  }

  const initialSearchPathname =
    route.kind === 'zone'
      ? `/homes/zid_${route.zoneId}`
      : `/homes/${resolvedMarket ?? route.market.toLowerCase()}`;

  const mapRefreshEnabled = mapRefreshForRequest(process.env.COSMOS_MAP_REFRESH_ENABLED, context);
  const initialVariables = buildPayloadFromQuery(
    { ...queryParams, ...getInitialPropertyTypes(queryParams) },
    {
      market: route.kind === 'market' ? resolvedMarket : null,
      vertices: hasViewportPolygon ? queryParams.vertices : null,
      zoneIds: hasViewportPolygon ? null : initialZoneIds,
    },
  );
  const viewportVertices = hasViewportPolygon ? (queryParams.vertices ?? []) : [];
  const viewportSamplePoints = getViewportSamplePoints(viewportVertices);
  const viewportSpanKm = getViewportSpanKm(viewportVertices);
  const fetchViewportAreaName = async (): Promise<string | null> => {
    if (viewportSamplePoints.length === 0) {
      return null;
    }
    try {
      const signal = AbortSignal.timeout(VIEWPORT_AREA_NAME_TIMEOUT_MS);
      const responses = await Promise.all(
        viewportSamplePoints.map((point) =>
          requestZeusFromServer(
            MarketplaceViewportAreaNameDocument,
            { input: getViewportAreaNameInput(point, viewportSpanKm) },
            { incomingRequestHeaders: context.req.headers, signal },
          ),
        ),
      );
      return getViewportAreaName(responses, viewportSpanKm);
    } catch (error) {
      Sentry.captureException(error, {
        tags: { feature: 'marketplace-browse', operation: 'viewportAreaName' },
      });
      return null;
    }
  };
  const queryClient = new QueryClient();
  let activeMarkets: ActiveMarket[];
  let initialViewportAreaName: string | null;
  let lastListingPage = 1;
  let ssrListingCount: number | null = null;
  let stagedPhotosExposure: StagedPhotosExposureData | null = null;
  try {
    const [browse, markets, areaName] = await Promise.all([
      // oxlint-disable-next-line typescript/no-deprecated -- The data layer remains on the current query-client API until its version migration
      queryClient.fetchInfiniteQuery(
        marketplaceBrowseQueryOptions({
          variables: initialVariables,
          mapMode: mapRefreshEnabled ? 'adaptive' : 'markers',
          mapViewport: mapRefreshEnabled ? getViewportFromShape(marketInfo.shape) : undefined,
          request: (document, variables, signal) =>
            requestZeusFromServer(document, variables, {
              incomingRequestHeaders: context.req.headers,
              signal,
            }),
        }),
      ),
      activeMarketsPromise,
      fetchViewportAreaName(),
    ]);
    activeMarkets = markets;
    initialViewportAreaName = areaName;
    const totalCount = browse.pages[0]?.listings.totalCount ?? 0;
    lastListingPage = Math.max(1, Math.ceil(totalCount / LISTINGS_PAGE_SIZE));
    if (!hasHomesMarketSeoFilters(queryParams) && totalCount > 0) {
      ssrListingCount = totalCount;
    }
    stagedPhotosExposure = stagedPhotosExposureFromEdge({
      addressIds: (browse.pages[0]?.listings.listings ?? []).flatMap((listing) =>
        listing.stagedPhotosReplaceShownPhotos ? [listing.addressId] : [],
      ),
      headers: context.req.headers,
    });
  } catch (error) {
    Sentry.captureException(error, {
      tags: { feature: 'marketplace-browse', operation: 'initialBrowse' },
    });
    throw error;
  }

  // A page past the end is either a probe or a link indexed before inventory
  // shrank, and neither is worth guessing an intent for. Sending it to the first
  // page matches what an unusable `page` already does — 0, a negative, and a
  // non-number all normalize away — instead of leaving an empty result set
  // indexable.
  const requestedPage = queryParams.page;
  if (requestedPage != null && requestedPage > lastListingPage) {
    return {
      redirect: {
        // No URL seeding: `buildQueryParams` reads `window.location` when it
        // seeds, and there is no window here.
        destination: appendSavedSearchId(
          buildCanonicalSearchPath(
            initialSearchPathname,
            { ...queryParams, page: null },
            { seedFromUrl: false },
          ),
          savedSearchId,
        ),
        permanent: false,
      },
    };
  }

  const robots = canonicalOverride ? homesMarketRobotsDirective(canonicalOverride) : null;
  if (robots) {
    context.res.setHeader('X-Robots-Tag', robots);
  }

  return {
    props: {
      dehydratedState: dehydrate(queryClient),
      initialVariables,
      initialSearchPathname,
      marketInfo,
      queryParams,
      activeMarkets,
      canonicalOverride,
      savedSearchId,
      initialZoneIds,
      initialViewportAreaName,
      ssrListingCount,
      mapRefreshEnabled,
      stagedPhotosExposure,
    },
  };
};

const Page: React.FC<MarketplacePageProps> = (props) => {
  const [queryClient] = useState(
    () =>
      new QueryClient({
        defaultOptions: { queries: { retry: 1, staleTime: 30_000 } },
        queryCache: createMarketplaceQueryCache(),
      }),
  );

  return (
    <QueryClientProvider client={queryClient}>
      <HydrationBoundary state={props.dehydratedState}>
        <StagedPhotosExposure exposure={props.stagedPhotosExposure} />
        <MarketplacePage key={buildMarketplacePageKey(props)} {...props} />
      </HydrationBoundary>
    </QueryClientProvider>
  );
};

export default Page;
